import React, { Component, createRef } from 'react';
import css from './VideoCallDrawer.css';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  answer,
  disconnectCall,
  offer,
  rejectCall,
  setup,
  setVideoRefs,
  toggleCallDrawer,
} from '../../../custom-ducks/webrtc.duck';
import {
  faExpand,
  faCompress,
  faPhone,
  faPhoneSlash,
  faTimes,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
  faImage,
  faUser,
  faUserSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CallAnimation from './call_animation.gif';
import BackgroundProcessor from './background-processor';

class VideoCallDrawer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullscreen: false,
      audioMuted: false,
      videoOff: false,
      selfViewHidden: false,
      // 'none' | 'blur' | 'image'
      backgroundMode: 'none',
      bgMenuOpen: false,
      bgError: '',
      bgLoading: false,
    };

    this.remoteVideoRef = createRef();
    this.localVideoRef = createRef();
    this.fileInputRef = createRef();

    // Plain refs (no React reconciliation needed) — these hold long-lived
    // objects we own outside of state.
    this.bgImage = null;       // HTMLImageElement decoded from the upload
    this.processor = null;     // BackgroundProcessor instance

    this.openToggle = this.openToggle.bind(this);
    this.toggleAudio = this.toggleAudio.bind(this);
    this.toggleVideo = this.toggleVideo.bind(this);
    this.toggleSelfView = this.toggleSelfView.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.handlePickBackgroundImage = this.handlePickBackgroundImage.bind(this);
    this.setBackgroundMode = this.setBackgroundMode.bind(this);
    this.toggleBgMenu = this.toggleBgMenu.bind(this);
    this.closeBgMenu = this.closeBgMenu.bind(this);
  }

  componentDidMount() {
    const { onSetup, onSetVideoRes, peerConnection } = this.props;
    onSetVideoRes({ local: this.localVideoRef, remote: this.remoteVideoRef });
    if (!peerConnection || peerConnection.signalingState === 'closed') {
      onSetup();
    }
    document.addEventListener('mousedown', this.closeBgMenu);
  }

  componentDidUpdate(prevProps) {
    // When the call ends, reset effect state so the next call starts clean.
    if (prevProps.callOngoing && !this.props.callOngoing) {
      this.teardownProcessor();
      this.setState({
        audioMuted: false,
        videoOff: false,
        selfViewHidden: false,
        backgroundMode: 'none',
        bgMenuOpen: false,
        bgError: '',
        bgLoading: false,
      });
    }

    // If the local stream gets swapped while a background effect is active,
    // rebuild the pipeline against the new stream.
    if (
      this.state.backgroundMode !== 'none' &&
      this.props.localVideoStream &&
      prevProps.localVideoStream !== this.props.localVideoStream
    ) {
      // Drop the old processor — it's bound to the old stream — and start fresh.
      this.teardownProcessor();
      this.applyBackgroundMode(this.state.backgroundMode);
    }
  }

  componentWillUnmount() {
    this.teardownProcessor();
    document.removeEventListener('mousedown', this.closeBgMenu);
  }

  openToggle() {
    const { isCallDrawerOpen, onToggleCallDrawer } = this.props;
    onToggleCallDrawer(!isCallDrawerOpen);
  }

  toggleFullscreen() {
    this.setState(s => ({ isFullscreen: !s.isFullscreen }));
  }

  // ---- mic / camera / self-view ----------------------------------------

  toggleAudio() {
    const { localVideoStream } = this.props;
    if (!localVideoStream) return;
    const tracks = localVideoStream.getAudioTracks();
    if (tracks.length === 0) return;
    const nextMuted = !this.state.audioMuted;
    tracks.forEach(t => { t.enabled = !nextMuted; });
    this.setState({ audioMuted: nextMuted });
  }

  toggleVideo() {
    // We flip enabled on BOTH the raw camera track and the processed track so
    // the local preview goes black at the same moment the remote does.
    // Setting enabled=false makes the track send black frames at minimal bitrate
    // instead of stopping it, which avoids renegotiation.
    const { localVideoStream } = this.props;
    if (!localVideoStream) return;
    const nextOff = !this.state.videoOff;
    localVideoStream.getVideoTracks().forEach(t => { t.enabled = !nextOff; });
    if (this.processor && this.processor.outputStream) {
      this.processor.outputStream.getVideoTracks().forEach(t => { t.enabled = !nextOff; });
    }
    this.setState({ videoOff: nextOff });
  }

  toggleSelfView() {
    this.setState(s => ({ selfViewHidden: !s.selfViewHidden }));
  }

  // ---- background pipeline ---------------------------------------------

  toggleBgMenu(e) {
    e.stopPropagation();
    this.setState(s => ({ bgMenuOpen: !s.bgMenuOpen }));
  }

  closeBgMenu(e) {
    if (!this.state.bgMenuOpen) return;
    // Bail when the click is inside the menu OR on the trigger button. We
    // detect both via data-attributes rather than CSS-module class names so
    // this works regardless of whether the bundler emits a particular hashed
    // class. Without the trigger-check, mousedown would close the menu just
    // before React's onClick re-opens it (net result: menu won't close on
    // re-click of the same button).
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-vc-bg-menu]')) return;
    if (e.target.closest('[data-vc-bg-trigger]')) return;
    this.setState({ bgMenuOpen: false });
  }

  handlePickBackgroundImage(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.setState({ bgError: 'Please pick an image (JPG, PNG, WebP).' });
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      // Pre-decode so the processor doesn't paint a half-loaded image on the
      // first frame.
      const img = new Image();
      img.onload = () => {
        this.bgImage = img;
        this.setState({ bgError: '' });
        this.setBackgroundMode('image');
      };
      img.onerror = () => this.setState({ bgError: 'Failed to load image.' });
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    if (this.fileInputRef.current) this.fileInputRef.current.value = '';
  }

  async setBackgroundMode(mode) {
    this.setState({ bgMenuOpen: false });

    if (mode === 'none') {
      this.setState({ backgroundMode: 'none', bgError: '' });
      this.teardownProcessor();
      return;
    }

    // For image mode, require the image first.
    if (mode === 'image' && !this.bgImage) {
      this.setState({ bgError: '' });
      if (this.fileInputRef.current) this.fileInputRef.current.click();
      return;
    }

    this.setState({ backgroundMode: mode, bgError: '', bgLoading: true });
    await this.applyBackgroundMode(mode);
    this.setState({ bgLoading: false });
  }

  async applyBackgroundMode(mode) {
    const { localVideoStream } = this.props;
    if (!localVideoStream) {
      this.setState({ bgError: 'Camera stream not ready yet.' });
      return;
    }
    try {
      if (!this.processor) {
        this.processor = new BackgroundProcessor();
        await this.processor.start(localVideoStream);
      }
      this.processor.setMode(mode, this.bgImage);

      const processedStream = this.processor.outputStream;
      if (this.localVideoRef.current) {
        this.localVideoRef.current.srcObject = processedStream;
        this.localVideoRef.current.play().catch(() => {});
      }
      const sender = this.getVideoSender();
      const processedTrack = processedStream.getVideoTracks()[0];
      if (sender && processedTrack && sender.track !== processedTrack) {
        await sender.replaceTrack(processedTrack);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Background effect failed:', err);
      this.setState({
        backgroundMode: 'none',
        bgError: 'Background effects are not available in this browser.',
      });
      this.teardownProcessor();
    }
  }

  teardownProcessor() {
    if (!this.processor) return;
    this.restoreRawSender();
    try { this.processor.stop(); } catch (e) { /* noop */ }
    this.processor = null;
  }

  getVideoSender() {
    const { peerConnection } = this.props;
    if (!peerConnection || !peerConnection.getSenders) return null;
    return (
      peerConnection.getSenders().find(s => s.track && s.track.kind === 'video') || null
    );
  }

  restoreRawSender() {
    const { localVideoStream } = this.props;
    const sender = this.getVideoSender();
    const raw = localVideoStream && localVideoStream.getVideoTracks()[0];
    if (sender && raw && sender.track !== raw) {
      sender.replaceTrack(raw).catch(err => {
        // eslint-disable-next-line no-console
        console.warn('replaceTrack(raw) failed:', err);
      });
    }
    if (this.localVideoRef.current && localVideoStream) {
      this.localVideoRef.current.srcObject = localVideoStream;
      this.localVideoRef.current.play().catch(() => {});
    }
  }

  // ---- render -----------------------------------------------------------

  render() {
    const {
      isCallDrawerOpen,
      onAnswer,
      onDisconnectCall,
      callOngoing,
      callIncoming,
      isShowable,
      onReject,
      currentRemoteUser,
    } = this.props;
    const {
      isFullscreen,
      audioMuted,
      videoOff,
      selfViewHidden,
      backgroundMode,
      bgMenuOpen,
      bgError,
      bgLoading,
    } = this.state;

    let baseStyle = {};
    baseStyle.display = isShowable ? 'block' : 'none';

    if (isCallDrawerOpen) {
      baseStyle.bottom = '0';
    } else if (isFullscreen) {
      baseStyle.bottom = 'calc(-100vh + 30px)';
    } else {
      baseStyle.bottom = '-400px';
    }

    if (isFullscreen) {
      baseStyle.left = '0';
      baseStyle.height = '100vh';
      baseStyle.width = '100vw';
      baseStyle.borderRadius = '0';
    }

    const localClasses = [
      css.localVideo,
      selfViewHidden ? css.localVideoHidden : '',
      videoOff ? css.localVideoOff : '',
    ].filter(Boolean).join(' ');

    return (
      <>
        {callIncoming && (
          <div className={css.call_incoming}>
            <div className={css.call_action}>
              <span style={{ marginTop: '20px' }}>Incoming call from {callIncoming.name}</span>
              <img alt="call animation" className={css.call_animation} src={CallAnimation} />
              <div className={css.call_actions}>
                <div onClick={() => onReject(callIncoming.id)} className={css.cancel_call_action}>
                  <FontAwesomeIcon icon={faPhoneSlash} />
                </div>
                <div onClick={() => onAnswer(callIncoming.id)} className={css.accept_call_action}>
                  <FontAwesomeIcon icon={faPhone} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={css.base} style={baseStyle}>
          <div onClick={this.openToggle} className={css.toggleHandle}>
            {callOngoing && currentRemoteUser && 'Ongoing call with ' + currentRemoteUser.name}
          </div>

          <div className={css.call_base}>
            <video
              className={css.remoteVideo}
              ref={this.remoteVideoRef}
              autoPlay
              playsInline
            />

            <div className={localClasses}>
              <video
                ref={this.localVideoRef}
                autoPlay
                playsInline
                muted
                className={css.localVideoEl}
              />
              {videoOff && (
                <div className={css.localVideoOffOverlay}>
                  <FontAwesomeIcon icon={faVideoSlash} />
                  <span>Camera off</span>
                </div>
              )}
              <div
                className={css.selfViewToggle}
                onClick={this.toggleSelfView}
                title={selfViewHidden ? 'Show self-view' : 'Hide self-view'}
              >
                <FontAwesomeIcon icon={selfViewHidden ? faUser : faUserSlash} />
              </div>
            </div>

            {/* When self-view is hidden, surface a small "show me" pill so the
                user can get it back without poking around the toolbar. */}
            {selfViewHidden && (
              <div className={css.selfViewRestore} onClick={this.toggleSelfView}>
                <FontAwesomeIcon icon={faUser} />
                <span>Show self-view</span>
              </div>
            )}

            {bgError && <div className={css.bgError}>{bgError}</div>}

            <div className={css.toolbar}>
              <div
                className={`${css.toolButton} ${audioMuted ? css.toolButtonDanger : ''}`}
                onClick={this.toggleAudio}
                title={audioMuted ? 'Unmute microphone' : 'Mute microphone'}
              >
                <FontAwesomeIcon icon={audioMuted ? faMicrophoneSlash : faMicrophone} />
              </div>

              <div
                className={`${css.toolButton} ${videoOff ? css.toolButtonDanger : ''}`}
                onClick={this.toggleVideo}
                title={videoOff ? 'Turn camera on' : 'Turn camera off'}
              >
                <FontAwesomeIcon icon={videoOff ? faVideoSlash : faVideo} />
              </div>

              <div
                className={`${css.toolButton} ${selfViewHidden ? css.toolButtonActive : ''}`}
                onClick={this.toggleSelfView}
                title={selfViewHidden ? 'Show self-view' : 'Hide self-view'}
              >
                <FontAwesomeIcon icon={selfViewHidden ? faUserSlash : faUser} />
              </div>

              <div className={css.bgWrapper}>
                <div
                  data-vc-bg-trigger
                  className={`${css.toolButton} ${
                    backgroundMode !== 'none' ? css.toolButtonActive : ''
                  } ${bgLoading ? css.toolButtonLoading : ''}`}
                  onClick={this.toggleBgMenu}
                  title="Change background"
                >
                  <FontAwesomeIcon icon={faImage} />
                </div>

                {bgMenuOpen && (
                  <div data-vc-bg-menu className={css.bgMenu}>
                    <div
                      className={`${css.bgMenuItem} ${backgroundMode === 'none' ? css.bgMenuItemActive : ''}`}
                      onClick={() => this.setBackgroundMode('none')}
                    >
                      None
                    </div>
                    <div
                      className={`${css.bgMenuItem} ${backgroundMode === 'blur' ? css.bgMenuItemActive : ''}`}
                      onClick={() => this.setBackgroundMode('blur')}
                    >
                      Blur background
                    </div>
                    <div
                      className={`${css.bgMenuItem} ${backgroundMode === 'image' ? css.bgMenuItemActive : ''}`}
                      onClick={() => this.setBackgroundMode('image')}
                    >
                      {this.bgImage ? 'Replace image…' : 'Upload background image…'}
                    </div>
                  </div>
                )}
              </div>

              <div
                className={css.toolButton}
                onClick={this.toggleFullscreen}
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
              </div>

              <div
                className={`${css.toolButton} ${css.toolButtonHangup}`}
                onClick={() => onDisconnectCall(currentRemoteUser && currentRemoteUser.id)}
                title="End call"
              >
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </div>

            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              onChange={this.handlePickBackgroundImage}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = state => {
  const {
    isCallDrawerOpen,
    remoteVideoStream,
    localVideoStream,
    callOngoing,
    peerConnection,
    callIncoming,
    isShowable,
    currentRemoteUser,
  } = state.webrtc;
  return {
    isCallDrawerOpen,
    remoteVideoStream,
    localVideoStream,
    callOngoing,
    peerConnection,
    callIncoming,
    isShowable,
    currentRemoteUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onToggleCallDrawer: isOpen => dispatch(toggleCallDrawer(isOpen)),
  onAnswer: remoteUUID => dispatch(answer(remoteUUID)),
  onReject: remoteUUID => dispatch(rejectCall(remoteUUID)),
  onMakeCall: remoteUUID => dispatch(offer(remoteUUID)),
  onSetup: () => dispatch(setup()),
  onDisconnectCall: remoteUUID => dispatch(disconnectCall(remoteUUID)),
  onSetVideoRes: videRefs => dispatch(setVideoRefs(videRefs)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withRouter)(VideoCallDrawer);
